// @flow
import queryString from 'query-string';
import type {
  UserMini,
  ImageData,
  ImageWithTextData,
  SlideshowData
} from 'api/shared';
import { getJson, putJson, postJson, del } from 'utils/http';

export type Layout = 'FULL_WIDTH' | 'NARROW' | 'FULL_WIDTH_PLUS';

export type HeroListingType =
  | 'JOB'
  | 'INTERNSHIP'
  | 'VOLOP'
  | 'ORG'
  | 'GROUP'
  | 'IDEALIST_DAY_EVENT'
  | 'GRAD_PROGRAM'
  | 'ALL';

export type HeroImageType = HeroListingType | 'GRAD_SCHOOLS';

export type HeroImages = {
  [HeroImageType]: ?ImageData
};

type RichTextType = 'RICHTEXT';
type ImageType = 'IMAGE';
type BlockquoteType = 'BLOCKQUOTE';
type GradCtaHeroType = 'GRAD_CTA_HERO';
type CtaHeroType = 'CTA_HERO';
type StickyHeaderLinksType = 'STICKY_HEADER_LINKS';
type StickyHeaderLinksGradType = 'STICKY_HEADER_LINKS_GRAD';
type AccordionType = 'ACCORDION';
type AnchorType = 'ANCHOR';
type VideoType = 'VIDEO';
type ThreeStepCtaType = 'THREE_STEP_CTA';
type TwoTilesType = 'TWO_TILES';
type FreeCodeType = 'FREE_CODE';
type EmailCampaignSignupSingleType = 'EMAIL_CAMPAIGN_SIGNUP_SINGLE';
type EmailCampaignSignupDoubleType = 'EMAIL_CAMPAIGN_SIGNUP_DOUBLE';
type FormBuilderType = 'FORM_BUILDER';
type ThreeSectionSliderType = 'THREE_SECTION_SLIDER';
type RandomGradProgramsType = 'RANDOM_GRAD_PROGRAMS';
type RecommendedActionsType = 'RECOMMENDED_ACTIONS';
type FourArticlesHighlightType = 'FOUR_ARTICLES_HIGHLIGHT';
type CategoryHighlightType = 'CATEGORY_HIGHLIGHT';
type IdealistDaysMapType = 'IDEALIST_DAYS_MAP';
type SplitContentType = 'SPLIT_CONTENT';
type ImageGalleryType = 'IMAGE_GALLERY';
type FlashCardQuizType = 'FLASH_CARD_QUIZ';
type PersonaQuizType = 'PERSONA_QUIZ';
type TileGridType = 'TILE_GRID';
type SupportContactFormType = 'SUPPORT_CONTACT_FORM';
type ScheduledContentType = 'SCHEDULED_CONTENT';
type DonateType = 'DONATE';
type GradFairGradProgramLinksType = 'GRAD_FAIR_GRAD_PROGRAM_LINKS';
type SignUpType = 'SIGN_UP';
type ScrollTrackingType = 'SCROLL_TRACKING';

export type BlockType =
  | RichTextType
  | ImageType
  | BlockquoteType
  | GradCtaHeroType
  | CtaHeroType
  | StickyHeaderLinksType
  | StickyHeaderLinksGradType
  | AccordionType
  | AnchorType
  | VideoType
  | ThreeStepCtaType
  | TwoTilesType
  | FreeCodeType
  | EmailCampaignSignupSingleType
  | EmailCampaignSignupDoubleType
  | FormBuilderType
  | ThreeSectionSliderType
  | RandomGradProgramsType
  | RecommendedActionsType
  | FourArticlesHighlightType
  | CategoryHighlightType
  | IdealistDaysMapType
  | SplitContentType
  | FlashCardQuizType
  | PersonaQuizType
  | TileGridType
  | ImageGalleryType
  | SupportContactFormType
  | ScheduledContentType
  | DonateType
  | GradFairGradProgramLinksType
  | SignUpType
  | ScrollTrackingType;

export type RichTextBlockData = {| html: string |};

export type ImageDataContent = ImageData & {|
  altText: ?string,
  caption: ?string
|};

export type ImageBlockData = {|
  headline?: string,
  subtitle?: string,
  image: ImageDataContent,
  url: ?string,
  width: 'NARROW' | 'WIDE'
|};

// @NOTE: I am not sure it makes sense to use
// ImageDataContent because the
// CTA hero currently does not support a
// caption, but it circumvents issues with
// two types in a disjointed union having
// a naming collision with their keys (in
// this case, `image`)

export type GradCtaHeroBlockData = {|
  image: ImageDataContent,
  text: string,
  withOverlay: boolean
|};

export type CtaHeroBlockData = GradCtaHeroBlockData & {|
  buttonText: ?string,
  buttonUrl: ?string
|};

export type BlockquoteBlockData = {| quote: string, cite: ?string |};

export type StickyHeaderLinkData = {
  heading: string,
  subHeading: string,
  url: string
};

export type StickyHeaderLinksGradBlockData = {|
  links: Array<StickyHeaderLinkData>
|};

export type StickyHeaderLinksBlockData = StickyHeaderLinksGradBlockData & {|
  buttonText?: string,
  buttonUrl?: string
|};

type AccordionItem = {
  title: string,
  content: string
};

export type AccordionItems = Array<AccordionItem>;

export type AccordionBlockData = {|
  default: 'ALL_CLOSED' | 'FIRST_ONE_OPENED',
  headline: ?string,
  subtitle: ?string, // RichText
  sections: AccordionItems
|};

export type AnchorBlockData = {|
  anchor: string
|};

export type VideoBlockData = {|
  headline: ?string,
  subtitle: ?string, // RichText
  videoUrl: string,
  width: 'NARROW' | 'WIDE'
|};

export type ThreeStepCtaBlockData = {|
  imageSize: 'SMALL' | 'LARGE',
  mainHeadline: string,
  mainText: string, // RichText
  buttonText: ?string,
  buttonUrl: ?string,
  stepOneImage: ImageData,
  stepOneHeadline: string,
  stepOneText: string, // RichText
  stepOneUrl: string,
  stepTwoImage: ImageData,
  stepTwoHeadline: string,
  stepTwoText: string, // RichText
  stepTwoUrl: string,
  stepThreeImage: ImageData,
  stepThreeHeadline: string,
  stepThreeText: string, // RichText
  stepThreeUrl: string
|};

export type TwoTilesBlockData = {|
  tileOneHeadline: string,
  tileOneText: string, // RichText
  tileOneButtonText: ?string,
  tileOneButtonUrl: ?string,
  tileTwoHeadline: string,
  tileTwoText: string, // RichText
  tileTwoButtonText: ?string,
  tileTwoButtonUrl: ?string
|};

export type EmailCampaignSignupSingleBlockData = {|
  headline: string,
  buttonText: string,
  campaignId: string,
  listId: string,
  bgImage: ?ImageData
|};

export type EmailCampaignSignupDoubleBlockData = {|
  headline: string,
  toggleTextOne: string,
  buttonTextOne: string,
  campaignIdOne: string,
  listIdOne: string,
  toggleTextTwo: string,
  buttonTextTwo: string,
  campaignIdTwo: string,
  listIdTwo: string,
  bgImage: ?ImageData
|};

type FormBuilderFieldOption = {
  value: ?string,
  label: string
};

export type FormBuilderField = {
  type: 'TEXT' | 'SELECT' | 'EMAIL' | 'CHECKBOX' | 'HIDDEN',
  name: string,
  label: string,
  placeholder: ?string,
  required: boolean,
  value: ?string,
  options?: Array<FormBuilderFieldOption>
};

export type FormBuilderBlockData = {|
  headline: ?string,
  submitButtonText: ?string,
  action: string,
  method: 'POST' | 'GET',
  newTab: boolean,
  fields: Array<FormBuilderField>,
  bgImage: ?ImageData
|};

export type ThreeSectionSliderImageSizes = 'MEDIUM' | 'SMALL';

export type ThreeSectionSliderSection = {|
  image: ?ImageData,
  content: string // RichText
|};

export type ThreeSectionSliderBlockData = {|
  headline: ?string,
  imageSize: ThreeSectionSliderImageSizes,
  sections: Array<ThreeSectionSliderSection>
|};

export type RandomGradProgramsBlockData = {|
  areasOfStudy: ?(string[]),
  headline: ?string
|};

export type RecommendedActionsBlockData = {|
  headline: ?string
|};

export type FourArticlesHighlightBlockData = {|
  headline: ?string,
  useRecentArticles: boolean,
  pageOneId: ?string,
  pageOneTitle: ?string,
  pageOneImage: ?ImageData,
  pageTwoId: ?string,
  pageTwoTitle: ?string,
  pageTwoImage: ?ImageData,
  pageThreeId: ?string,
  pageThreeTitle: ?string,
  pageThreeImage: ?ImageData,
  pageFourId: ?string,
  pageFourTitle: ?string,
  pageFourImage: ?ImageData,
  buttonUrl: ?string,
  buttonText: ?string
|};

type CategoryHighlightItem = {
  id: string,
  image: ImageData,
  description: string,
  pageIds: Array<string>
};

type CategoryHighlightPageItem = {
  id: string,
  title: string
};

export type CategoryHighlightData = {|
  headline: ?string,
  categories: Array<CategoryHighlightItem>,
  pages: Array<CategoryHighlightPageItem>
|};

type CategoryHighlightBlockPageItem = {
  id: string,
  title: string,
  url: string
};

export type CategoryHighlightBlockItem = {
  id: string,
  name: string,
  slug: string,
  image: ?ImageData,
  description: string,
  pages: Array<CategoryHighlightBlockPageItem>
};

export type CategoryHighlightBlockData = {|
  headline: ?string,
  categories: Array<CategoryHighlightBlockItem>
|};

export type FreeCodeBlockData = {
  code: string,
  isVisible: boolean
};

export type ButtonLinkData = {
  data: {
    url: string,
    pageTitle: ?string,
    s3FileId?: string,
    redirectUrl?: string
  },
  text: string,
  type: 'CMS_PAGE' | 'URL' | 'S3_FILE' | 'SUBSCRIBE'
};

type SplitBlockText = {
  type: 'TEXT',
  data: {
    label: ?string,
    headline: ?string,
    html: string,
    button: ?ButtonLinkData
  }
};

type SplitBlockVideo = {
  type: 'VIDEO',
  data: {
    url: string
  }
};

type SplitBlockImage = {
  type: 'IMAGE',
  data: {
    image: ImageWithTextData,
    size: 'CROPPED' | 'FIXED_WIDTH'
  }
};

type SplitBlockBlockquote = {
  type: 'BLOCKQUOTE',
  data: {|
    quote: string,
    cite: ?string
  |}
};

export type SplitContentSection =
  | SplitBlockText
  | SplitBlockVideo
  | SplitBlockImage
  | SplitBlockBlockquote;

export type SplitBlockAppearance =
  | 'SIDE_BY_SIDE'
  | 'OVERLAPPING'
  | 'FULL_WIDTH';

export type SplitContentBlockData = {|
  appearance: SplitBlockAppearance,
  sections: Array<SplitContentSection>
|};

export type ImageGalleryBlockData = {|
  images: Array<SlideshowData>
|};

export type ScheduledContentBlockData = {|
  queueId: string
|};

export type FlashCard = {|
  instructions: string,
  image: ImageData,
  question: string, // RichText
  answer: string // RichText
|};

export type FlashCardQuizBlockData = {|
  cards: Array<FlashCard>
|};

export type ShareLinkData = {
  data: {
    url: string,
    pageTitle: ?string,
    pageId: ?string
  },
  text: string,
  type: 'CMS_PAGE' | 'URL'
};

export type IdealistDaysMapBlockData = {|
  prompt: ?string,
  mapType: 'DAYS' | 'GROUPS',
  mapId: string,
  instructions: string, // RichText
  buttonText: ?string,
  defaultFullscreen: boolean,
  socialShareMessage: ?string,
  socialShareLink: ?ShareLinkData
|};

export type PersonaAnswer = {|
  title: string,
  points: Array<number>
|};

export type PersonaQuestion = {|
  title: string,
  image: ImageData,
  answers: Array<PersonaAnswer>
|};

export type PersonaResult = {|
  title: string,
  image: ImageData,
  description: string, // RichText
  socialMediaText: string,
  tempId?: string
|};

export type PersonaQuizBlockData = {|
  questions: Array<PersonaQuestion>,
  results: Array<PersonaResult>
|};

export type TileGridTile = {|
  heading: string,
  subHeading: string,
  description: string, // RichText
  image: ImageData,
  thumbnail: ImageData
|};

export type TileGridSection = {|
  name: string,
  tiles: Array<TileGridTile>
|};

export type TileGridBlockData = {|
  imageSize: 'LARGE' | 'SMALL',
  sections: Array<TileGridSection>
|};

export type DonateBlockData = {|
  headline: string, // RichText
  body: string, // RichText
  thankYouHeadline: string, // RichText
  thankYouBody: string, // RichText
  campaignId: string,
  bgImage: ImageData
|};

export type GradFairGradProgramLinksBlockData = {|
  fairId: string,
  locationId: string
|};

export type SignUpBlockData = {|
  bgImage: ImageData,
  headline: string, // RichText
  redirectUrl: string
|};

export type ScrollTrackingBlockData = {|
  eventName: string
|};

type ImageBlock = { id: string, type: ImageType, data: ImageBlockData };
type RichTextBlock = {
  id: string,
  type: RichTextType,
  data: RichTextBlockData
};
type BlockquoteBlock = {
  id: string,
  type: BlockquoteType,
  data: BlockquoteBlockData
};
type StickyHeaderLinksBlock = {
  id: string,
  type: StickyHeaderLinksType,
  data: StickyHeaderLinksBlockData
};
type StickyHeaderLinksGradBlock = {
  id: string,
  type: StickyHeaderLinksGradType,
  data: StickyHeaderLinksGradBlockData
};
type AccordionBlock = {
  id: string,
  type: AccordionType,
  data: AccordionBlockData
};
type CtaHeroBlock = { id: string, type: CtaHeroType, data: CtaHeroBlockData };
type GradCtaHeroBlock = {
  id: string,
  type: GradCtaHeroType,
  data: GradCtaHeroBlockData
};
type ThreeStepCtaBlock = {
  id: string,
  type: ThreeStepCtaType,
  data: ThreeStepCtaBlockData
};
type TwoTilesBlock = {
  id: string,
  type: TwoTilesType,
  data: TwoTilesBlockData
};
type AnchorBlock = { id: string, type: AnchorType, data: AnchorBlockData };
type VideoBlock = { id: string, type: VideoType, data: VideoBlockData };
type FreeCodeBlock = {
  id: string,
  type: FreeCodeType,
  data: FreeCodeBlockData
};
type EmailCampaignSignupSingleBlock = {
  id: string,
  type: EmailCampaignSignupSingleType,
  data: EmailCampaignSignupSingleBlockData
};
type EmailCampaignSignupDoubleBlock = {
  id: string,
  type: EmailCampaignSignupDoubleType,
  data: EmailCampaignSignupDoubleBlockData
};
type ThreeSectionSliderBlock = {
  id: string,
  type: ThreeSectionSliderType,
  data: ThreeSectionSliderBlockData
};
type FourArticlesHighlightBlock = {
  id: string,
  type: FourArticlesHighlightType,
  data: FourArticlesHighlightBlockData
};
type RandomGradProgramsBlock = {
  id: string,
  type: RandomGradProgramsType,
  data: RandomGradProgramsBlockData
};
type RecommendedActionsBlock = {
  id: string,
  type: RecommendedActionsType,
  data: RecommendedActionsBlockData
};
type CategoryHighlightBlock = {
  id: string,
  type: CategoryHighlightType,
  data: CategoryHighlightBlockData
};
type IdealistDaysMapBlock = {
  id: string,
  type: IdealistDaysMapType,
  data: IdealistDaysMapBlockData
};
export type SplitContentBlock = {
  id: string,
  type: SplitContentType,
  data: SplitContentBlockData
};
type ImageGalleryBlock = {
  id: string,
  type: ImageGalleryType,
  data: ImageGalleryBlockData
};
type FlashCardQuizBlock = {
  id: string,
  type: FlashCardQuizType,
  data: FlashCardQuizBlockData
};
type PersonaQuizBlock = {
  id: string,
  type: PersonaQuizType,
  data: PersonaQuizBlockData
};
type ScheduledContentBlock = {
  id: string,
  type: ScheduledContentType,
  data: ScheduledContentBlockData
};
type DonateBlock = {
  id: string,
  type: DonateType,
  data: DonateBlockData
};
type GradFairGradProgramLinksBlock = {
  id: string,
  type: GradFairGradProgramLinksType,
  data: GradFairGradProgramLinksBlockData
};
type SignUpBlock = {
  id: string,
  type: SignUpType,
  data: SignUpBlockData
};
type ScrollTrackingBlock = {
  id: string,
  type: ScrollTrackingType,
  data: ScrollTrackingBlockData
};

export type PageContentBlock =
  | ImageBlock
  | RichTextBlock
  | BlockquoteBlock
  | StickyHeaderLinksBlock
  | StickyHeaderLinksGradBlock
  | AccordionBlock
  | CtaHeroBlock
  | GradCtaHeroBlock
  | ThreeStepCtaBlock
  | TwoTilesBlock
  | AnchorBlock
  | VideoBlock
  | FreeCodeBlock
  | EmailCampaignSignupSingleBlock
  | EmailCampaignSignupDoubleBlock
  | ThreeSectionSliderBlock
  | FourArticlesHighlightBlock
  | RandomGradProgramsBlock
  | RecommendedActionsBlock
  | CategoryHighlightBlock
  | IdealistDaysMapBlock
  | SplitContentBlock
  | ImageGalleryBlock
  | FlashCardQuizBlock
  | PersonaQuizBlock
  | ScheduledContentBlock
  | DonateBlock
  | GradFairGradProgramLinksBlock
  | SignUpBlock
  | ScrollTrackingBlock;

export type PageContentBlockData =
  | ImageBlockData
  | RichTextBlockData
  | BlockquoteBlockData
  | StickyHeaderLinksBlockData
  | StickyHeaderLinksGradBlockData
  | AccordionBlockData
  | CtaHeroBlockData
  | GradCtaHeroBlockData
  | ThreeStepCtaBlockData
  | TwoTilesBlockData
  | AnchorBlockData
  | VideoBlockData
  | FreeCodeBlockData
  | EmailCampaignSignupSingleBlockData
  | EmailCampaignSignupDoubleBlockData
  | ThreeSectionSliderBlockData
  | FourArticlesHighlightBlockData
  | RandomGradProgramsBlockData
  | RecommendedActionsBlockData
  | CategoryHighlightBlockData
  | IdealistDaysMapBlockData
  | SplitContentBlockData
  | ImageGalleryBlockData
  | FlashCardQuizBlockData
  | PersonaQuizBlockData
  | ScheduledContentBlockData
  | DonateBlockData
  | GradFairGradProgramLinksBlockData
  | SignUpBlockData
  | ScrollTrackingBlockData;

export type MiniPage = {
  created: string,
  updated: string,
  title: string,
  id: string,
  type: 'PAGE' | 'POST',
  translations: Array<MiniPage>,
  url: string,
  locale: string,
  isPublished: boolean,
  canBePublished: boolean,
  canBeUnpublished: boolean,
  postedDate: string,
  scheduledPublishing?: ?string
};

export type MiniHeroSearch = {
  created: string,
  name: string,
  id: string
};

export type HeroSearch = {
  created: string,
  name: string,
  id: string,
  text: string,
  image: ?ImageData,
  imageMobile: ?ImageData,
  onlyResults: boolean,
  showCounts: boolean,
  defaultSearchFilters: { type: string }, // @TODO: still needed after launch?
  cta?: React.Node
};

export type HeroSearchInfo = {
  name: string,
  text: string,
  type: string,
  image: ImageData
};

export type CategoryInfo = {
  name: string,
  slug: string,
  parentId: string
};

export type Category = {
  id: string,
  name: string,
  locale: string,
  slug: string,
  parentId: ?string,
  childrenIds: Array<string>
};

// Payload for editing tag
export type TagInfo = {
  oldTag: string,
  newTag: string,
  slug: string
};

// Data returned when viewing tag list in CMS
export type CmsTag = {
  tag: string,
  slug: string,
  locale: string
};

// Tag shape when retrieved from public endpoint
export type PublicTag = {
  tag: string,
  slug: string
};

export type Author = {
  id: string,
  name: string,
  slug: string,
  description: string, // RichText
  image: ?ImageData
};

export type AuthorInfo = {
  name: string,
  slug: string,
  description: string, // RichText
  image: ?ImageData
};

export type Redirect = {
  id: string,
  created: string,
  detectLocale: boolean,
  fromDomain: ?string,
  fromUrl: string,
  object: ?{ id: string, title: string, url: string },
  toForeignType: 'CMS_PAGE' | 'URL',
  note: ?string,
  toForeignId: ?string,
  toUrl: ?string,
  lastUpdatedBy: UserMini,
  updated: string
};

export type RedirectInfo = {
  id: string,
  created: string,
  detectLocale: boolean,
  fromUrl: string,
  toForeignType: 'CMS_PAGE' | 'URL',
  note: ?string,
  toForeignId: ?string,
  toUrl: ?string
};

type RichTextSidebarBlock = RichTextBlock & { section: 'TOP' | 'BOTTOM' };
type CtaHeroSidebarBlock = CtaHeroBlock & { section: 'TOP' | 'BOTTOM' };
type EmailCampaignSignupSingleSidebarBlock = EmailCampaignSignupSingleBlock & {
  section: 'TOP' | 'BOTTOM'
};
type EmailCampaignSignupDoubleSidebarBlock = EmailCampaignSignupDoubleBlock & {
  section: 'TOP' | 'BOTTOM'
};

export type SidebarContentBlock =
  | RichTextSidebarBlock
  | CtaHeroSidebarBlock
  | EmailCampaignSignupSingleSidebarBlock
  | EmailCampaignSignupDoubleSidebarBlock;

export type Sidebar = {
  id: string,
  created: number,
  updated: number,
  name: string,
  contentBlocksTop: Array<SidebarContentBlock>,
  contentBlocksBottom: Array<SidebarContentBlock>
};

export type SidebarInfo = {
  name: string,
  contentBlocksTop: Array<SidebarContentBlock>,
  contentBlocksBottom: Array<SidebarContentBlock>
};

export type Subsite = {
  canHaveSearch: boolean,
  created: string,
  defaultLocale: string,
  defaultSearchType: ListingType,
  defaultSidebarId: string,
  hasRecommendedJobs: boolean,
  hasSocialSharing: boolean,
  homePageIds: Array<string>,
  homePages: Array<MiniPage>,
  id: string,
  includedSubsiteIds: Array<string>,
  name: string,
  pages: Array<MiniPage>,
  posts: Array<MiniPage>,
  publicName: { [UserLocale]: string },
  urlRoot: string,
  visibleToRobots: boolean,
  placeholderImages: Array<ImageDataContent>
};

export type SubsiteInfo = {
  layout: Layout,
  defaultLocale: string,
  name: string,
  url: string
};

export type PageType = 'PAGE' | 'POST';
export type PageStatus = 'ALL' | 'DRAFT' | 'PUBLISHED' | 'SCHEDULED';

export type Page = {
  categoryIds: Array<Category>,
  created: string,
  updated: string,
  layout: Layout,
  hasSearch: boolean,
  translations: Array<MiniPage>,
  title: string,
  showTitle: boolean,
  id: string,
  url: string,
  contentBlocks: Array<PageContentBlock>,
  locale: string,
  masterId: string,
  subsite: Subsite,
  heroSearchId: ?string,
  overrideUrl: boolean,
  overrideSidebar: boolean,
  sidebarId: string,
  headerImage: ?ImageWithTextData,
  metaDescription: ?string,
  thumbnail: ?ImageData,
  headTitle: ?string,
  visibleToRobots: boolean,
  hasSocialSharing: boolean,
  onlySearchResults: boolean,
  author?: Author,
  authorId?: string,
  postedDate: string,
  updated: string,
  showDate: boolean,
  scheduledPublishing?: ?string,
  tags: Array<CmsTag>,
  type: PageType,
  isPublished: boolean,
  canBePublished: boolean,
  canBeUnpublished: boolean,
  hasRecommendedJobs: boolean,
  isSearchable: boolean,
  ampEnabled: boolean,
  recommendedJobsHeadline: string,
  associatedSubsiteIds: Array<string>
};

export type PageInfo = {
  categoryIds: Array<Category>,
  layout: Layout,
  title: string,
  url: string,
  contentBlocks: Array<PageContentBlock>,
  overrideUrl: boolean,
  heroSearchId: ?string,
  sidebarId: string,
  visibleToRobots: boolean,
  authorId?: string,
  postedDate?: ?string
};

export type CmsSubscribe = {
  blockId: string,
  index: number,
  redirectUrl?: string
};

export function fetchSubsites(): Promise<Array<Subsite>> {
  return getJson('/data/cms/sub-sites').then(({ subsites }) => subsites);
}

export function fetchSubsite(subsiteId: string): Promise<Subsite> {
  return getJson(`/data/cms/sub-sites/${subsiteId}`).then(
    ({ subsite }) => subsite
  );
}

export function createSubsite(subsiteInfo: SubsiteInfo): Promise<Subsite> {
  return postJson('/data/cms/sub-sites', subsiteInfo).then(
    ({ subsite }) => subsite
  );
}

export function updateSubsite(
  subsiteId: string,
  subsiteInfo: SubsiteInfo
): Promise<Subsite> {
  return putJson(`/data/cms/sub-sites/${subsiteId}`, subsiteInfo).then(
    ({ subsite }) => subsite
  );
}

export function deleteSubsite(subsiteId: string): Promise<BackgroundTask> {
  return del(`/data/cms/sub-sites/${subsiteId}`).then(({ task }) => task);
}

export function fetchPageById(pageId: string): Promise<Page> {
  return getJson(`/data/cms/pages/${pageId}`).then(({ page }) => page);
}

export function createPage(
  subsiteId: string,
  pageInfo: PageInfo
): Promise<Page> {
  return postJson(`/data/cms/sub-sites/${subsiteId}/pages`, pageInfo).then(
    ({ page }) => page
  );
}

export function updatePage(pageId: string, pageInfo: PageInfo): Promise<Page> {
  return putJson(`/data/cms/pages/${pageId}`, pageInfo).then(
    ({ page }) => page
  );
}

export function deletePage(pageId: string) {
  return del(`/data/cms/pages/${pageId}`);
}

export function fetchHeroSearches(): Promise<Array<MiniHeroSearch>> {
  return getJson('/data/cms/hero-searches').then(
    ({ heroSearches }) => heroSearches
  );
}

export function fetchHeroSearch(heroSearchId: string): Promise<HeroSearch> {
  return getJson(`/data/cms/hero-searches/${heroSearchId}`).then(
    ({ heroSearch }) => heroSearch
  );
}

export function createHeroSearch(
  heroSearchInfo: HeroSearchInfo
): Promise<HeroSearch> {
  return postJson(`/data/cms/hero-searches`, heroSearchInfo).then(
    ({ heroSearch }) => heroSearch
  );
}

export function updateHeroSearch(
  heroSearchId: string,
  heroSearchInfo: HeroSearchInfo
): Promise<HeroSearch> {
  return putJson(
    `/data/cms/hero-searches/${heroSearchId}`,
    heroSearchInfo
  ).then(({ heroSearch }) => heroSearch);
}

export function fetchSubsiteSidebars(
  subsiteId: string
): Promise<Array<Sidebar>> {
  return getJson(`/data/cms/sub-sites/${subsiteId}/sidebars`).then(
    ({ sidebars }) => sidebars
  );
}

export function fetchSidebar(sidebarId: string): Promise<Sidebar> {
  return getJson(`/data/cms/sidebars/${sidebarId}`).then(
    ({ sidebar }) => sidebar
  );
}

export function createSidebar(
  subsiteId: string,
  sidebarInfo: SidebarInfo
): Promise<Sidebar> {
  return postJson(
    `/data/cms/sub-sites/${subsiteId}/sidebars`,
    sidebarInfo
  ).then(({ sidebar }) => sidebar);
}

export function updateSidebar(
  sidebarId: string,
  sidebarInfo: SidebarInfo
): Promise<Sidebar> {
  return putJson(`/data/cms/sidebars/${sidebarId}`, sidebarInfo).then(
    ({ sidebar }) => sidebar
  );
}

export function fetchSubsiteCategories(
  subsiteId: string
): Promise<Array<Category>> {
  return getJson(`/data/cms/sub-sites/${subsiteId}/categories`).then(
    ({ categories }) => categories
  );
}

export function fetchCategory(categoryId: string): Promise<Category> {
  return getJson(`/data/cms/categories/${categoryId}`).then(
    ({ category }) => category
  );
}

export function createCategory(
  subsiteId: string,
  categoryInfo: CategoryInfo
): Promise<Category> {
  return postJson(
    `/data/cms/sub-sites/${subsiteId}/categories`,
    categoryInfo
  ).then(({ category }) => category);
}

export function updateCategory(
  categoryId: string,
  categoryInfo: CategoryInfo
): Promise<Category> {
  return putJson(`/data/cms/categories/${categoryId}`, categoryInfo).then(
    ({ category }) => category
  );
}

export function deleteCategory(categoryId: string) {
  return del(`/data/cms/categories/${categoryId}`);
}

export function deleteHeroSearch(heroSearchId: string) {
  return del(`/data/cms/hero-searches/${heroSearchId}`);
}

export function fetchSubsiteTags(
  subsiteId: string,
  locale: string,
  params: any
): Promise<{
  tags: Array<CmsTag>,
  count: number,
  lastPage: number,
  page: number
}> {
  return getJson(
    `/data/cms/sub-sites/${subsiteId}/tags?locale=${locale}&${queryString.stringify(
      params
    )}`
  );
}

export function updateTag(
  tagInfo: TagInfo,
  subsiteId: string,
  locale: string
): Promise<CmsTag> {
  return putJson(
    `/data/cms/sub-sites/${subsiteId}/tags?locale=${locale}`,
    tagInfo
  );
}

export function deleteTag(tag: CmsTag, subsiteId: string) {
  return postJson(
    `/data/cms/sub-sites/${subsiteId}/tags/delete?locale=${tag.locale}`,
    {
      tag: tag.tag
    }
  );
}

export const getSubscribeUrl = (subsiteId: string) =>
  `/data/website/subsite-subscribe-email/${subsiteId}`;

export function subscribeToSubsite(subsiteId: string) {
  return postJson(getSubscribeUrl(subsiteId));
}

export function fetchAuthors(): Promise<Array<Author>> {
  return getJson('/data/cms/authors').then(({ authors }) => authors);
}

export function fetchAuthor(authorId: string): Promise<Author> {
  return getJson(`/data/cms/authors/${authorId}`).then(({ author }) => author);
}

export function createAuthor(authorInfo: AuthorInfo): Promise<Author> {
  return postJson(`/data/cms/authors`, authorInfo).then(({ author }) => author);
}

export function updateAuthor(
  authorId: string,
  authorInfo: AuthorInfo
): Promise<Author> {
  return putJson(`/data/cms/authors/${authorId}`, authorInfo).then(
    ({ author }) => author
  );
}

export function publishPage(pageId: string): Promise<Page> {
  return postJson(`/data/cms/pages/${pageId}/publish`).then(({ page }) => page);
}

export function unpublishPage(pageId: string): Promise<Page> {
  return postJson(`/data/cms/pages/${pageId}/unpublish`).then(
    ({ page }) => page
  );
}

export function createRedirect(redirectInfo: RedirectInfo): Promise<Redirect> {
  return postJson(`/data/cms/redirects`, redirectInfo).then(
    ({ redirect }) => redirect
  );
}

export function fetchRedirects(
  page: ?number
): Promise<{
  redirects: Array<Redirect>,
  count: number,
  lastPage: number,
  page: number
}> {
  // Response has JSON with keys {redirects, count, lastPage, page}
  return getJson(
    page ? `/data/cms/redirects?page=${page}` : '/data/cms/redirects'
  );
}

export function updateRedirect(
  redirectId: string,
  redirectInfo: RedirectInfo
): Promise<Redirect> {
  return putJson(`/data/cms/redirects/${redirectId}`, redirectInfo).then(
    ({ redirect }) => redirect
  );
}

export function fetchRedirect(redirectId: string): Promise<Redirect> {
  return getJson(`/data/cms/redirects/${redirectId}`).then(
    ({ redirect }) => redirect
  );
}

export function deleteRedirect(redirectId: string): Promise<Redirect> {
  return del(`/data/cms/redirects/${redirectId}`);
}

export function fetchPagesByMasterId(masterIds: Array<string>) {
  const params = queryString.stringify({ id: masterIds });
  return getJson(`/data/cms/pages-by-master-id?${params}`).then(
    ({ pages }) => pages
  );
}

export const fetchGradFairs = () =>
  getJson('/data/cms/fairs').then(({ fairs }) => fairs);
